import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const terms = [
  { term: 'ACHC', definition: 'Accreditation Commission for Health Care — a nonprofit accreditation organization with CMS deeming authority for home health, hospice, pharmacy, and other healthcare services.' },
  { term: 'CMS', definition: 'Centers for Medicare & Medicaid Services — the federal agency that administers Medicare, Medicaid, and the Children\'s Health Insurance Program (CHIP).' },
  { term: 'CoPs', definition: 'Conditions of Participation — the federal health and safety standards that healthcare providers must meet to participate in Medicare and Medicaid programs (42 CFR Part 484 for home health).' },
  { term: 'Deeming Authority', definition: 'The authority granted by CMS to accreditation organizations to certify that accredited providers meet Medicare Conditions of Participation, eliminating the need for a separate state survey.' },
  { term: 'Gap Analysis', definition: 'A systematic assessment comparing an agency\'s current operations against required standards to identify deficiencies that must be addressed before accreditation.' },
  { term: 'HHCAHPS', definition: 'Home Health Consumer Assessment of Healthcare Providers and Systems — a standardized patient satisfaction survey used to measure and publicly report patient experience in home health.' },
  { term: 'IDT', definition: 'Interdisciplinary Team — the group of healthcare professionals (nurses, therapists, social workers, etc.) who collaborate to develop and implement a patient\'s plan of care.' },
  { term: 'MAC', definition: 'Medicare Administrative Contractor — a private company that processes Medicare claims and handles Medicare enrollment for providers in a specific geographic region.' },
  { term: 'Mock Survey', definition: 'A simulated accreditation survey conducted by a consultant to identify compliance gaps and prepare staff before the actual ACHC survey.' },
  { term: 'OASIS', definition: 'Outcome and Assessment Information Set — a standardized data collection tool used by home health agencies to assess patient health status and measure outcomes.' },
  { term: 'PIP', definition: 'Performance Improvement Project — a focused, structured effort to improve a specific aspect of care or operations, required as part of a QAPI program.' },
  { term: 'Plan of Correction', definition: 'A formal document submitted to ACHC after a survey that describes how an agency will address each cited deficiency, including specific actions, responsible parties, and timelines.' },
  { term: 'QAPI', definition: 'Quality Assessment and Performance Improvement — a systematic, data-driven approach to identifying problems and implementing measurable improvements in care quality and operations.' },
  { term: 'RN', definition: 'Registered Nurse — a licensed healthcare professional who provides skilled nursing services and supervises home health aide services in home health settings.' },
  { term: 'Skilled Care', definition: 'Healthcare services that require the expertise of a licensed professional (RN, PT, OT, SLP) and cannot be safely performed by an untrained person.' },
  { term: 'SOC', definition: 'Start of Care — the initial visit by a home health clinician to assess a new patient and begin the care planning process, triggering an OASIS assessment.' },
  { term: 'Surveyor', definition: 'An ACHC-employed or contracted healthcare professional who conducts on-site accreditation surveys to evaluate an agency\'s compliance with ACHC standards.' },
  { term: 'TJC', definition: 'The Joint Commission — the largest healthcare accreditation organization in the United States, also holding CMS deeming authority for home health agencies.' },
  { term: 'Unannounced Survey', definition: 'An accreditation survey conducted without prior notice to the agency — ACHC conducts unannounced surveys as part of its accreditation process.' },
  { term: 'WOCN', definition: 'Wound, Ostomy and Continence Nurse — a registered nurse with specialized certification in wound care, ostomy care, and continence management.' },
];

export default function AchcGlossary() {
  const [search, setSearch] = useState('');

  useSEO({
    title: 'ACHC & Home Health Glossary: Key Terms & Acronyms | Advantixx',
    description: 'A comprehensive reference guide to key terms, acronyms, and concepts used in ACHC accreditation and home health compliance — from ACHC to WOCN.',
    keywords: 'ACHC glossary, home health terms, accreditation acronyms, ACHC terminology, home health compliance glossary',
    canonical: '/resources/achc-glossary',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-glossary';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/resources/achc-glossary#webpage`,
      url: `${siteUrl}/resources/achc-glossary`,
      name: 'ACHC & Home Health Glossary: Key Terms & Acronyms | Advantixx',
      description: 'A comprehensive reference guide to key terms, acronyms, and concepts used in ACHC accreditation and home health compliance.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: {
        '@type': 'Thing',
        name: 'ACHC Accreditation Terminology',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Advantixx Home Health Accreditation Consultants',
        url: siteUrl,
      },
    });
    document.head.appendChild(script);
    return () => { document.getElementById('schema-glossary')?.remove(); };
  }, []);

  const filtered = terms.filter(t =>
    t.term.toLowerCase().includes(search.toLowerCase()) ||
    t.definition.toLowerCase().includes(search.toLowerCase())
  );

  const letters = [...new Set(filtered.map(t => t.term[0].toUpperCase()))].sort();

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />
      <section className="pt-32 pb-16 max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">Free Resource</p>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-5">ACHC &amp; Home Health <span className="text-[#d4af37]">Glossary</span></h1>
          <p className="text-[#888] text-base max-w-2xl mx-auto leading-relaxed">A comprehensive reference guide to the key terms, acronyms, and concepts used in ACHC accreditation and home health compliance.</p>
        </div>

        {/* Search */}
        <div className="relative mb-10 max-w-xl mx-auto">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
            <i className="ri-search-line text-[#555] text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search terms..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#141414] border border-[#2a2a2a] rounded-full pl-11 pr-5 py-3 text-sm text-[#ccc] placeholder-[#444] outline-none focus:border-[#d4af37]/50 transition-colors duration-200"
          />
        </div>

        {/* Letter Nav */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {letters.map(letter => (
            <a key={letter} href={`#letter-${letter}`} className="w-8 h-8 flex items-center justify-center bg-[#141414] border border-[#2a2a2a] rounded-md text-[#888] text-xs font-bold hover:border-[#d4af37]/40 hover:text-[#d4af37] transition-colors duration-200 cursor-pointer">{letter}</a>
          ))}
        </div>

        {/* Terms */}
        <div className="flex flex-col gap-3">
          {letters.map(letter => (
            <div key={letter} id={`letter-${letter}`}>
              <div className="flex items-center gap-3 mb-3 mt-6">
                <span className="text-[#d4af37] font-black text-2xl">{letter}</span>
                <div className="flex-1 h-px bg-[#1e1e1e]"></div>
              </div>
              {filtered.filter(t => t.term[0].toUpperCase() === letter).map((item, i) => (
                <div key={i} className="bg-[#141414] border border-[#1e1e1e] rounded-xl p-5 mb-3">
                  <h2 className="text-[#d4af37] font-bold text-sm mb-2">{item.term}</h2>
                  <p className="text-[#888] text-sm leading-relaxed">{item.definition}</p>
                </div>
              ))}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#555] text-sm">No terms found matching &quot;{search}&quot;</p>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center mt-12">
          <h3 className="text-white font-black text-xl mb-3">Have Questions About ACHC Accreditation?</h3>
          <p className="text-[#888] text-sm mb-6">Our team is here to help you navigate the accreditation process with confidence.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">Contact Us <i className="ri-arrow-right-line"></i></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
